import { useLayoutEffect, useMemo, useRef } from 'react';
import { BufferGeometry, InstancedMesh, Matrix4, Vector3, Quaternion, Euler } from 'three';
import type { HardwareGroup } from './instances';
import { useSceneSelection } from '../selection/useSceneSelection';
import {
  rivetDome3_16,
  rivetDome1_4,
  rivetCountersunk3_16,
  rivetCountersunk1_4,
} from './primitives/rivetGeometry';
import { SCALE_IN_TO_UNIT } from '../types';
import { materialForHardwareSku } from '../materials';

const GEOMETRY_FACTORY: Record<string, () => BufferGeometry> = {
  'rivet-dome-3-16': rivetDome3_16,
  'rivet-dome-1-4': rivetDome1_4,
  'rivet-countersunk-3-16': rivetCountersunk3_16,
  'rivet-countersunk-1-4': rivetCountersunk1_4,
};

const VISUAL_SCALE = 5; // rivets ×5 to be visible from camera distance

export function RivetField({ group }: { group: HardwareGroup }) {
  const ref = useRef<InstancedMesh>(null);
  const factory = GEOMETRY_FACTORY[group.geometry];
  const geometry = useMemo(() => (factory ? factory() : null), [factory, group.geometry]);

  // R1 Bug 4 fix: pick material based on the hardware SKU prefix.
  // QRAL = aluminum rivet (bright), QRSS = stainless (very bright).
  // The legacy synthetic prefix "RIVET-3-16-DOME" falls through to
  // steelAnodized as before so existing layouts don't visually regress.
  const material = materialForHardwareSku(group.partNumber);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const m = new Matrix4();
    group.placements.forEach((p, i) => {
      m.compose(
        new Vector3(
          p.position.x * SCALE_IN_TO_UNIT,
          p.position.y * SCALE_IN_TO_UNIT,
          p.position.z * SCALE_IN_TO_UNIT,
        ),
        new Quaternion().setFromEuler(new Euler(p.rotation.x, p.rotation.y, p.rotation.z)),
        new Vector3(VISUAL_SCALE, VISUAL_SCALE, VISUAL_SCALE),
      );
      ref.current!.setMatrixAt(i, m);
    });
    ref.current.instanceMatrix.needsUpdate = true;
  }, [group.placements]);

  if (!geometry || group.placements.length === 0) return null;

  return (
    <instancedMesh
      ref={ref}
      args={[geometry, material, group.placements.length]}
      castShadow
      userData={{ partNumber: group.partNumber, kind: 'hardware-field', geometry: group.geometry }}
      onPointerOver={(e) => {
        e.stopPropagation();
        const id = e.instanceId;
        if (id == null) return;
        const ref = group.placements[id]?.parentRefId ?? group.partNumber;
        useSceneSelection.getState().setHovered(ref);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        useSceneSelection.getState().setHovered(null);
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        const id = e.instanceId;
        if (id == null) return;
        const ref = group.placements[id]?.parentRefId ?? group.partNumber;
        useSceneSelection.getState().setSelected(ref);
      }}
    />
  );
}
